import { useEffect } from 'react';
import { LoanApplicationProvider, useLoanApplication } from '../../contexts/LoanApplicationContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProgressIndicator from './components/ProgressIndicator';
import Step1PersonalInfo from './components/Step1PersonalInfo';
import Step2LoanDetails from './components/Step2LoanDetails';
import Step3DocumentUpload from './components/Step3DocumentUpload';
import Step4ReviewSubmit from './components/Step4ReviewSubmit';

function ApplyContent() {
  const { currentStep, applicationData } = useLoanApplication();

  // Form abandonment warning
  useEffect(() => {
    const hasData = 
      applicationData.personalInfo.firstName !== '' ||
      applicationData.personalInfo.email !== '' ||
      applicationData.loanDetails.loanAmount !== undefined;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasData && currentStep < 4) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [applicationData, currentStep]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1PersonalInfo />;
      case 2:
        return <Step2LoanDetails />;
      case 3:
        return <Step3DocumentUpload />;
      case 4:
        return <Step4ReviewSubmit />;
      default:
        return <Step1PersonalInfo />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-1 py-12" role="main" aria-label="Loan Application Form">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Loan Application</h1>
              <p className="text-gray-600">Complete the form below to apply for a loan</p>
            </div>

            {/* Progress Indicator */}
            <ProgressIndicator />

            {/* Step Content */}
            <div className="mt-8">
              {renderStep()}
            </div>

            {/* RBZ Compliance Notice */}
            {currentStep < 4 && (
              <div className="mt-6 text-center text-sm text-gray-500" role="note" aria-label="Security Notice">
                <p>
                  <i className="ri-shield-check-line mr-1" aria-hidden="true"></i>
                  Your information is protected in compliance with RBZ regulations and data protection standards
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default function ApplyPage() {
  return (
    <LoanApplicationProvider>
      <ApplyContent />
    </LoanApplicationProvider>
  );
}